import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CotinHeader } from '@cotin/biblioteca-componentes-react';
import { useAuth } from '@/core/hooks/useAuth';
import { useAvatar } from '@/core/context/avatar/AvatarContext';
import { useSidebar } from '@/core/context/sidebar/SidebarContext';
import {
    PREFERENCIA_TUTORIAL,
    subscribePreferencias,
} from '@/services/preferencias.service';
import {
    getHeaderLogoPath,
} from '@/core/configs/brand-logos.config';
import {
    obterConfiguracoesSistema,
    subscribeBrasaoOficial,
} from '@/services/configuracoes-sistema.service';
import { startTutorial } from '@/services/tutorial.service';
import { getTutorialStepsForRoute } from '@/core/configs/tutorial/tutorial-steps';
import { getLoginAD } from '@/core/configs/auth/auth.utils';
import { Mensagem } from '@/core/models/mensagens/mensagem.model';
import {
    solicitarAtualizacaoNotificacoes,
    startMensagensNotificationPolling,
} from '@/services/mensagens.service';
import UserDropdown, { UserAvatarImage, useDisplayLoginAD } from './UserDropdown';
import ConfiguracoesModal from './ConfiguracoesModal';
import NotificationDropdown from './NotificationDropdown';

const Header: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { usuarioSessao } = useAuth();
    const { avatarUrl, setAvatarUrl } = useAvatar();
    const { toggleMobileSidebar } = useSidebar();
    const [showUserDropdown, setShowUserDropdown] = useState(false);
    const [showConfiguracoesModal, setShowConfiguracoesModal] = useState(false);
    const [tutorialAtivo, setTutorialAtivo] = useState(true);
    const [headerLogoSrc, setHeaderLogoSrc] = useState(getHeaderLogoPath(false));
    const [showNotificationModal, setShowNotificationModal] = useState(false);
    const [unreadMessages, setUnreadMessages] = useState<Mensagem[]>([]);
    const [notificationCount, setNotificationCount] = useState(0);
    const notificationBtnRef = useRef<HTMLButtonElement>(null);
    const displayLoginAD = useDisplayLoginAD(usuarioSessao);
    const loginAD = getLoginAD(usuarioSessao);
    const skipNotificationPolling = location.pathname === '/mensagens';

    useEffect(() => {
        const unsubscribe = subscribePreferencias((preferencias) => {
            const tutorialRaw = preferencias[PREFERENCIA_TUTORIAL];
            setTutorialAtivo(tutorialRaw === false ? false : true);
        });
        return unsubscribe;
    }, []);

    useEffect(() => {
        void obterConfiguracoesSistema();
        return subscribeBrasaoOficial((brasaoOficial) => {
            setHeaderLogoSrc(getHeaderLogoPath(brasaoOficial));
        });
    }, []);

    const openNotifications = () => {
        if (showNotificationModal) {
            setShowNotificationModal(false);
            return;
        }

        setShowNotificationModal(true);
        solicitarAtualizacaoNotificacoes();
    };

    useEffect(() => {
        if (!loginAD) {
            setUnreadMessages([]);
            setNotificationCount(0);
            return;
        }

        return startMensagensNotificationPolling({
            loginAD,
            enabled: !skipNotificationPolling,
            onData: (data) => {
                const allUnread = (data.items ?? []).filter((item) => !item.dataLeitura);
                setNotificationCount(allUnread.length);
                setUnreadMessages(allUnread.slice(0, 10));
            },
            onError: () => {
                setNotificationCount(0);
                setUnreadMessages([]);
            },
        });
    }, [loginAD, skipNotificationPolling]);

    const closeUserSidebar = () => {
        setShowUserDropdown(false);
    };

    const openConfiguracoes = () => {
        setShowConfiguracoesModal(true);
    };

    const closeConfiguracoes = useCallback(() => {
        setShowConfiguracoesModal(false);
    }, []);

    const openTutorial = () => {
        const steps = getTutorialStepsForRoute(location.pathname);
        if (steps.length > 0) {
            startTutorial(steps);
        }
    };

    const goHome = useCallback(() => {
        navigate('/home');
    }, [navigate]);

    const actionsContent = (
        <>
            {tutorialAtivo && (
                <button
                    type="button"
                    className="tutorial-btn"
                    onClick={openTutorial}
                    aria-label="Tutorial"
                >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                        <path
                            d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                        <circle
                            cx="12"
                            cy="17"
                            r="0.5"
                            fill="currentColor"
                            stroke="currentColor"
                            strokeWidth="1"
                        />
                    </svg>
                </button>
            )}

            <button
                ref={notificationBtnRef}
                type="button"
                className="notification-btn"
                onClick={openNotifications}
                aria-label="Notificações"
                aria-expanded={showNotificationModal}
            >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                        d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                    <path
                        d="M13.73 21a2 2 0 01-3.46 0"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
                {notificationCount > 0 && (
                    <span className="badge badge-danger rounded-pill notification-badge">
                        {notificationCount > 99 ? '99+' : notificationCount}
                    </span>
                )}
            </button>

            <span className="cotin-notification-host" aria-hidden="true">
                <NotificationDropdown
                    isOpen={showNotificationModal}
                    messages={unreadMessages}
                    anchorRef={notificationBtnRef}
                    onClose={() => setShowNotificationModal(false)}
                    onViewMessage={(message) => {
                        setShowNotificationModal(false);
                        navigate(`/mensagens/visualizar/${message.id}`);
                    }}
                    onViewAll={() => {
                        setShowNotificationModal(false);
                        navigate('/mensagens');
                    }}
                />
            </span>

            <div
                className="user-info"
                onClick={() => setShowUserDropdown(true)}
                role="button"
                tabIndex={0}
                aria-label="Perfil do usuário"
                onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        setShowUserDropdown(true);
                    }
                }}
            >
                <div className="user-details">
                    <div className="user-name">{displayLoginAD}</div>
                    <div className="user-role">{usuarioSessao?.nomeGrupo || ''}</div>
                </div>
                <div className="user-avatar">
                    <UserAvatarImage avatarUrl={avatarUrl} onError={() => setAvatarUrl(null)} />
                </div>
            </div>
        </>
    );

    return (
        <>
            <div className="header">
                <button
                    type="button"
                    className="mobile-hamburger-btn"
                    onClick={toggleMobileSidebar}
                    aria-label="Menu Mobile"
                >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                            d="M3 12h18M3 6h18M3 18h18"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                        />
                    </svg>
                </button>

                <div className="header__lib">
                    <CotinHeader
                        logoUrl={headerLogoSrc}
                        logoAlt="COTIN"
                        systemName={(usuarioSessao?.nomeSistema ?? '').trim()}
                        onLogoClick={goHome}
                        actionsContent={actionsContent}
                    />
                </div>
            </div>

            {showUserDropdown && (
                <>
                    <div
                        className={`user-sidebar-overlay ${showUserDropdown ? 'is-open' : ''}`}
                        onClick={closeUserSidebar}
                        role="presentation"
                    />
                    <aside className={`user-sidebar ${showUserDropdown ? 'is-open' : ''}`}>
                        <div className="user-sidebar-header">
                            <h2 className="user-sidebar-title">Perfil</h2>
                            <button
                                type="button"
                                className="user-sidebar-close"
                                onClick={closeUserSidebar}
                                aria-label="Fechar"
                            >
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path
                                        d="M18 6L6 18M6 6l12 12"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            </button>
                        </div>
                        <div className="user-sidebar-content" onClick={(event) => event.stopPropagation()}>
                            <UserDropdown inlineMode onOpenConfiguracoes={openConfiguracoes} />
                        </div>
                    </aside>
                </>
            )}

            <ConfiguracoesModal isOpen={showConfiguracoesModal} onClose={closeConfiguracoes} />
        </>
    );
};

export default Header;
